import type { FC, SVGProps } from "react"
import { useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import {
	IconCheckOutline18 as Check,
	IconCopyOutline18 as CopyIcon,
} from "nucleo-ui-outline-18"
import { useCopyToClipboard, useDebounce } from "react-use"

import { cn } from "@productlane/lib/src/cn"

import { Tooltip } from "./tooltip"

const iconTransition = { duration: 0.15, ease: [0.23, 1, 0.32, 1] } as const
const iconInitial = { opacity: 0, scale: 0.6, filter: "blur(4px)" }
const iconAnimate = { opacity: 1, scale: 1, filter: "blur(0px)" }

interface CopyButtonProps {
	value: string
	tooltip?: string
	className?: string
	iconClassName?: string
	icon?: LucideIcon
	text?: string
	onCopied?: () => void
}

export function CopyButton({
	value,
	tooltip,
	className,
	iconClassName,
	icon: Icon = CopyIcon,
	text,
	onCopied,
}: CopyButtonProps) {
	const [, copy] = useCopyToClipboard()
	const [isCopied, setIsCopied] = useState(false)
	useDebounce(
		() => {
			setIsCopied(false)
		},
		1000,
		[isCopied],
	)
	return (
		<Tooltip label={tooltip} enabled={!!tooltip}>
			<button
				type="button"
				className={cn(
					"relative inline-flex flex-none items-center justify-center rounded-md text-secondary hover:text-primary",
					text ? "gap-1.5 px-2.5 py-1.5 text-sm" : "h-7 w-7",
					className,
				)}
				onClick={() => {
					copy(value)
					setIsCopied(true)
					onCopied?.()
				}}
			>
				<AnimatePresence mode="wait" initial={false}>
					{isCopied ? (
						<motion.span
							key="check"
							initial={iconInitial}
							animate={iconAnimate}
							exit={iconInitial}
							transition={iconTransition}
							className="flex items-center justify-center"
						>
							<Check
								className={cn("h-4 w-4", iconClassName)}
								strokeWidth={2.5}
							/>
						</motion.span>
					) : (
						<motion.span
							key="copy"
							initial={iconInitial}
							animate={iconAnimate}
							exit={iconInitial}
							transition={iconTransition}
							className="flex items-center justify-center"
						>
							<Icon className={cn("h-4 w-4", iconClassName)} />
						</motion.span>
					)}
				</AnimatePresence>
				{text && <span>{text}</span>}
			</button>
		</Tooltip>
	)
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>
